import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  clearResearchAssistantIsProcessing,
  setResearchAssistantIsReady,
} from '@verantos-dev/app-state-research-assistant';
import { logError } from '@verantos-dev/core-utils';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';
import {
  ValidatedConceptGroupData,
  type ConceptGroupPreviewSummary,
} from '@verantos-dev/types-concepts';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';

export const useConceptGroupReviewListeners = (
  onMarkConceptGroupAsGenerated?: (conceptGroupName: string) => void
) => {
  const onMarkConceptGroupAsGeneratedRef = useRef(onMarkConceptGroupAsGenerated);
  onMarkConceptGroupAsGeneratedRef.current = onMarkConceptGroupAsGenerated;

  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { socket: currentSocket } = useResearchAssistantSocketContext();

  const conceptGroupReviewHandler = useCallback(
    (data: string) => {
      try {
        const parsed = JSON.parse(data);

        // Detect preview format by presence of previewId
        const isPreview = 'previewId' in parsed;

        if (isPreview) {
          const preview = parsed as ConceptGroupPreviewSummary;
          dispatch(
            addResearchAssistantMessages([
              {
                message: intl.formatMessage(
                  {
                    id: 'projects.researchAssistant.conceptGroupReview.message',
                  },
                  {
                    conceptGroupName: preview.conceptGroupName,
                  }
                ),
                timestamp: new Date().toISOString(),
                currentUser: 'research_assistant',
                isCurrentUser: false,
                type: ResearchAssistantMessageEnumLegacy.CONCEPT_GROUP_REVIEW,
                conceptGroupReviewData: {
                  conceptGroupName: preview.conceptGroupName,
                  concepts: preview.sampleConcepts,
                  filterKey: preview.filterKey,
                  previewId: preview.previewId,
                  totalCount: preview.totalCount,
                  domainBreakdown: preview.domainBreakdown,
                },
              },
            ])
          );
        } else {
          const validatedConceptGroup = parsed as ValidatedConceptGroupData;
          dispatch(
            addResearchAssistantMessages([
              {
                message: intl.formatMessage(
                  {
                    id: 'projects.researchAssistant.conceptGroupReview.message',
                  },
                  {
                    conceptGroupName: validatedConceptGroup.conceptGroupName,
                  }
                ),
                timestamp: new Date().toISOString(),
                currentUser: 'research_assistant',
                isCurrentUser: false,
                type: ResearchAssistantMessageEnumLegacy.CONCEPT_GROUP_REVIEW,
                conceptGroupReviewData: {
                  conceptGroupName: validatedConceptGroup.conceptGroupName,
                  concepts: validatedConceptGroup.concepts,
                  filterKey: validatedConceptGroup.filterKey,
                },
              },
            ])
          );
        }

        // Mark the concept group as generated via callback (works for both formats)
        const conceptGroupName = isPreview
          ? (parsed as ConceptGroupPreviewSummary).conceptGroupName
          : (parsed as ValidatedConceptGroupData).conceptGroupName;
        onMarkConceptGroupAsGeneratedRef.current?.(conceptGroupName);

        dispatch(setResearchAssistantIsReady(true));
        dispatch(clearResearchAssistantIsProcessing({}));
      } catch (error) {
        logError({ message: 'Failed to parse review_concept_group data', error: String(error) });
      }
    },
    [dispatch, intl]
  );

  useEffect(() => {
    if (currentSocket) {
      currentSocket.on('review_concept_group', conceptGroupReviewHandler);
    }

    return () => {
      if (currentSocket) {
        currentSocket.off('review_concept_group', conceptGroupReviewHandler);
      }
    };
  }, [conceptGroupReviewHandler, currentSocket]);
};
